import HorizontalScroll from "@Components/HorizontalScroll";
import Image from "@Components/Image";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useIsLanguage from "hooks/useIsLanguage";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import useTranslationHook from "hooks/useTranslationHook";
import { UIEvent, useRef } from "react";
import Subsection from "../Subsection";
import AmberPlusOfferCards from "./AmberPlusOfferCards";
import styles from "./AmberPlusOffers.module.scss";
import {
  CARD_STRIDE_DESKTOP,
  CARD_STRIDE_MOBILE,
  PRIMARY_AMBER_PLUS_BANNER,
  emitOfferClick,
  useAmberPlusOffers,
} from "./offers";

// Re-exported so the hosts that rail these cards themselves (ReferralProgram, Payment
// Success) can keep importing from the component directory.
export { AmberPlusOfferCards };
export { CARD_STRIDE_DESKTOP, CARD_STRIDE_MOBILE, useAmberPlusOffers } from "./offers";
export type { CmsOffer } from "./offers";

type AmberPlusOffersProps = {
  isDesktop?: boolean;
  greyBg?: boolean;
  className?: string;
};

/**
 * The standalone Amber+ section — own heading and own rail. Still used on Payment Success;
 * the home page now folds these cards into the ReferralProgram rail instead.
 */
const AmberPlusOffers = ({
  isDesktop = false,
  greyBg = true,
  className = "",
}: AmberPlusOffersProps) => {
  const { t } = useTranslationHook();
  const language = useIsLanguage();
  const { isAbove } = useResponsiveness();
  const isAboveMD = isAbove(SCREEN_SIZE.MD, false);
  const showForDesktop = isDesktop && isAboveMD;
  const offers = useAmberPlusOffers();
  const viewedCardDepthRef = useRef(1);
  // The hardcoded lead banner plus one card per CMS offer.
  const totalCards = offers.length ? offers.length + 1 : 0;
  const sectionTitle = (
    <>
      Amber<span className={styles["brand-plus"]}>+</span> Offers
    </>
  );

  const handleCarouselScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (!target || !totalCards) return;
    const cardWidth = showForDesktop ? CARD_STRIDE_DESKTOP : CARD_STRIDE_MOBILE;
    const visibleCard = Math.min(
      totalCards,
      Math.max(1, Math.ceil((target.scrollLeft + target.clientWidth) / cardWidth)),
    );
    if (visibleCard <= viewedCardDepthRef.current) return;
    viewedCardDepthRef.current = visibleCard;
    gtagEventEmitter({
      action: "amber_plus_carousel_scroll",
      scroll_depth: `viewed ${visibleCard} out of ${totalCards} cards`,
    });
  };

  if (!totalCards) return null;

  return (
    <Subsection
      title={sectionTitle}
      subtitle="Access travel essentials and services designed to make your move seamless."
      withSidePadding={showForDesktop}
      subSectionPaddingReduced={!showForDesktop}
      greyBg={greyBg}
      isDesktop={showForDesktop}
      className={className}
      noPaddingTop
      largeMarginBottom
    >
      <HorizontalScroll
        showControls={showForDesktop}
        className={formatClassNames(styles["horizontal-scroll"], showForDesktop && styles.desktop)}
        controlsClass={styles["controls-full-height"]}
        itemSize={showForDesktop ? CARD_STRIDE_DESKTOP : CARD_STRIDE_MOBILE}
        onHorizontalScroll={handleCarouselScroll}
      >
        <a
          href={PRIMARY_AMBER_PLUS_BANNER.href}
          target="_blank"
          rel="noreferrer"
          className={formatClassNames(styles["offer-card"], styles["first-offer-card"])}
          onClick={() => emitOfferClick("Amber+", 1, "General")}
        >
          <Image
            src={
              showForDesktop
                ? PRIMARY_AMBER_PLUS_BANNER.desktopBg
                : PRIMARY_AMBER_PLUS_BANNER.mobileBg
            }
            alt="amber plus"
            width={showForDesktop ? 356 : 314}
            height={showForDesktop ? 162 : 170}
            className={styles["banner-image"]}
            isEagerLoad
          />
          <div className={styles.overlay} />
          <div className={styles["first-content"]}>
            <div>
              <h3 className={formatClassNames(styles.title, styles["first-title"])}>
                {language !== "en"
                  ? t("home.reffralprogram.amberplus.title")
                  : "Save up to £1500 with amber+"}
              </h3>
              <p className={formatClassNames(styles.subtitle, styles["first-subtitle"])}>
                {t("home.reffralprogram.amberplus.subtitleone")}
                <br />
                {t("home.reffralprogram.amberplus.subtitletwo")}
              </p>
            </div>
            <span className={styles["first-cta"]}>Explore Services</span>
          </div>
        </a>
        {/* Lead banner occupies rail position 1, so partner cards start at 2. */}
        <AmberPlusOfferCards offers={offers} showForDesktop={showForDesktop} startIndex={2} />
      </HorizontalScroll>
    </Subsection>
  );
};

export default wrapperHOC(AmberPlusOffers, {
  componentName: "AmberPlusOffers-Home",
  showForChina: true,
});
